import React, { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTwoFactorEnrollMutation, useTwoFactorVerifyMutation } from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services/authService";
import { apiErrorMessage } from "@/util/apiError";
import { DEVICE_PLATFORM, getDeviceName, getDeviceToken, setDeviceToken } from "@/util/device";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { TwoFactorNavigationState } from "../model/interface/TwoFactor";

export default function TwoFactorEnrollForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuthStatus } = useAuth();
  const challenge = location.state as TwoFactorNavigationState | null;

  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState<string | undefined>(undefined);

  const [enroll, enrollResult] = useTwoFactorEnrollMutation();
  const [verify, verifyResult] = useTwoFactorVerifyMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmitPhone = (data: FieldValues) => {
    if (!challenge) {
      return;
    }
    enroll({ challengeToken: challenge.challengeToken, phone: data.phone });
  };

  const submitCode = (value: string) => {
    if (!challenge || verifyResult.isLoading) {
      return;
    }
    verify({
      challengeToken: challenge.challengeToken,
      code: value,
      rememberDevice,
      deviceToken: getDeviceToken() ?? undefined,
      deviceName: getDeviceName(),
      devicePlatform: DEVICE_PLATFORM,
    });
  };

  useEffect(() => {
    if (!challenge) {
      navigate("/login", { replace: true });
    }
  }, [challenge, navigate]);

  useEffect(() => {
    if (enrollResult.isSuccess) {
      setMaskedPhone(enrollResult.data.maskedPhone);
    }
  }, [enrollResult]);

  useEffect(() => {
    if (verifyResult.isSuccess) {
      const { accessToken, refreshToken, deviceToken } = verifyResult.data;
      authService.setAccessToken(accessToken);
      authService.setRefreshToken(refreshToken);
      if (deviceToken) {
        setDeviceToken(deviceToken);
      }
      refreshAuthStatus().then(() => navigate(challenge?.redirect ?? "/dashboard"));
    }
  }, [verifyResult, navigate, refreshAuthStatus, challenge]);

  if (!challenge) {
    return null;
  }

  const phoneSubmitted = enrollResult.isSuccess;

  return (
    <div className="w-full max-w-md space-y-6 text-foreground">
      <div className="flex justify-center">
        <Link to="/">
          <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
        </Link>
      </div>

      <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Secure your account</h1>
          <p className="text-sm text-muted-foreground">
            {phoneSubmitted
              ? `Enter the 6-digit code we sent to ${maskedPhone ?? "your phone"}.`
              : "Your organisation requires two-factor authentication. Add your mobile number to receive verification codes. SMS codes are billed at $0.01 each; you can switch to a free authenticator app in Settings afterwards."}
          </p>
        </div>

        {!phoneSubmitted && (
          <form onSubmit={handleSubmit(onSubmitPhone)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Mobile number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 555 123 4567"
                autoComplete="tel"
                required
                {...register("phone", { required: true, minLength: 7 })}
              />
              {errors.phone && (
                <p className="text-xs text-destructive">A valid mobile number is required</p>
              )}
            </div>

            {enrollResult.isError && (
              <p className="text-sm text-destructive">
                {apiErrorMessage(enrollResult.error, "Unable to send the code. Please try again.")}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={enrollResult.isLoading}>
              {enrollResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send verification code
            </Button>
          </form>
        )}

        {phoneSubmitted && (
          <div className="space-y-6">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={setCode}
                onComplete={submitCode}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="rememberDevice"
                checked={rememberDevice}
                onCheckedChange={(checked) => setRememberDevice(checked === true)}
              />
              <Label htmlFor="rememberDevice" className="text-sm font-normal">
                Trust this device for 30 days
              </Label>
            </div>

            {verifyResult.isError && (
              <p className="text-sm text-destructive">
                {apiErrorMessage(verifyResult.error, "Invalid code. Please try again.")}
              </p>
            )}

            <Button
              className="w-full"
              disabled={code.length < 6 || verifyResult.isLoading}
              onClick={() => submitCode(code)}
            >
              {verifyResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Verify and continue
            </Button>
          </div>
        )}

        <div className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="hover:text-foreground hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
